import React, { useEffect, useState } from 'react';
import { Table, message, Button, Popconfirm, Modal, Avatar, Space } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import CreateReportingOfficer from './CreateReportingOfficer';
import { config } from '../utils';
import {EyeOutlined, EditOutlined, DeleteOutlined, UserOutlined, UserAddOutlined } from '@ant-design/icons';


interface Officer {
    id: string;
    officer_name: string;
    email: string;
    phone_number: string;
    img?: string;
}

const OfficersList = () => {
    const [officers, setOfficers] = useState<Officer[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchOfficers();
    }, []);

    const fetchOfficers = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(config.api.endpoints.officers);
            setOfficers(response.data);
        } catch (error: any) {
            console.error('Error fetching officers:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response headers:', error.response.headers);
            }
            message.error('Failed to fetch officers');
        } finally {
            setLoading(false);
        }
    };

    const deleteOfficer = async (id: string) => {
        try {
            await axiosInstance.delete(`${config.api.endpoints.officers}/${id}`);
            message.success('Officer deleted successfully');
            fetchOfficers(); // Refresh the list
        } catch (error) {
            message.error('Failed to delete officer');
        }
    };

    const columns = [
        {
            title: 'Officer Name',
            dataIndex: 'officer_name',
            key: 'officer_name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone Number',
            dataIndex: 'phone_number',
            key: 'phone_number',
        },
        {
            title: 'Profile Image',
            dataIndex: 'img',
            key: 'img',
            render: (img: string) =>
                img ? (
                    <Avatar src={img} size={50} />
                ) : (
                    <Avatar icon={<UserOutlined />} size={50} />
                ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: any, record: Officer) => (
                <Space size="middle">
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/officers/${record.id}`)}
                    />
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/officers/edit/${record.id}`)}
                    />
                    <Popconfirm
                        title="Are you sure to delete this officer?"
                        onConfirm={() => deleteOfficer(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Functions to handle modal visibility
    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
    };

    // Function to refresh the list after adding an officer
    const handleOfficerAdded = () => {
        setIsModalVisible(false);
        fetchOfficers().then((r) => console.log('Officers fetched'));
    };

    return (
        <div className="p-8">
          
            <div className="flex justify-between items-center mb-6">
            <div className="flex items-center p-5 gap-2">
            <UserAddOutlined className="text-2xl text-red-600" />
               <p className="text-md text-black font-bold">  Reporting Officers</p>
            </div> 
            
            <div className="flex items-center p-5 gap-2">
            <Button onClick={() => navigate('/settings')} >
                Back 
            </Button>          
            <Button type="primary" onClick={showModal}>
                    Add Officer
                </Button>
                </div>

            </div>

            <Table
                dataSource={officers}
                columns={columns}
                rowKey="id"
                loading={loading}
            />

            {/* Modal for adding an officer */}
            <Modal
                title="Create Reporting Officer"
                open={isModalVisible}
                footer={null}
                onCancel={handleModalClose}
                destroyOnClose
                width={800}
            >
                <CreateReportingOfficer onSuccess={handleOfficerAdded} />
            </Modal>
        </div>
    );
};

export default OfficersList;